import React, { useState } from "react";
import { DataTable, HighlightMatch, IconButton, Table } from "@sscs/ui";
import {
  AddUsageLimitIcon,
  EditIcon,
  RemoveUsageLimitIcon,
} from "@sscs/ui/icons";
import { convertToReadable } from "@sscs/utils";
import { AllowedTo } from "Components";
import { PERMISSION } from "Constants";
import { useAllowedTo, useBelongsToGroup } from "Hooks";
import { useGetWhoamiQuery } from "Services";
import { GroupUsage } from "Types";
import GroupUsageStatus from "../GroupUsageStatus/GroupUsageStatus";
import ProjectsUsage from "../ProjectsUsage/ProjectsUsage";
import RemoveLimit from "../RemoveLimit/RemoveLimit";
import styles from "./groupRow.module.scss";

type GroupRowProps = {
  group: GroupUsage;
  focused: boolean;
  filterGroupName: string;
  onFocusChange: () => void;
  onAddLimit: () => void;
};

const GroupRow = ({
  group,
  focused,
  onFocusChange,
  onAddLimit,
  filterGroupName,
}: GroupRowProps): React.JSX.Element => {
  const [removeLimit, setRemoveLimit] =
    useState<NonNullable<GroupUsage> | null>(null);

  const { data: whoami } = useGetWhoamiQuery();

  const { belongsToGroup } = useBelongsToGroup({
    groupId: group.group_id,
    groups: whoami?.org_groups,
  });

  const hasPermissions = useAllowedTo({
    permissions: [PERMISSION.VIEW_GROUP_USAGE_DETAILS],
    groupId: group.group_id,
  });

  const canExpandRow = belongsToGroup || hasPermissions;

  return (
    <>
      {removeLimit?.limit && (
        <RemoveLimit
          groupUsage={removeLimit}
          isOpen={removeLimit !== null}
          onClose={() => setRemoveLimit(null)}
        />
      )}
      <DataTable.ExpandedRow
        focused={focused}
        onClick={onFocusChange}
        disabled={!canExpandRow}
      >
        <DataTable.ExpandedRow.Summary>
          <Table.Cell>
            <HighlightMatch value={group.group_name} regex={filterGroupName} />
          </Table.Cell>
          <Table.Cell>
            {group.limit ? convertToReadable(group.limit.limit_bytes) : "-"}
          </Table.Cell>
          <Table.Cell>{convertToReadable(group.total_usage_bytes)}</Table.Cell>
          <Table.Cell>
            <GroupUsageStatus
              percent={group.limit?.filestream_percentage}
              total={group.limit?.limit_bytes}
              used={group.filestream_usage_bytes}
            />
          </Table.Cell>
          <Table.Cell>
            <GroupUsageStatus
              percent={group.limit?.projects_percentage}
              total={group.limit?.limit_bytes}
              used={group.projects_usage_bytes}
            />
          </Table.Cell>
          <AllowedTo permissions={[PERMISSION.ADD_GROUP_CAPACITY_LIMIT]}>
            <Table.Cell align="center" aria-label="usage limit group actions">
              <IconButton
                tooltipTitle={`${
                  group.limit ? "Edit" : "Add"
                } group usage limit`}
                aria-label={`${group.limit ? "edit" : "add"} group usage limit`}
                onClick={onAddLimit}
                className={styles["icon-button"]}
              >
                {group.limit ? (
                  <EditIcon />
                ) : (
                  <AddUsageLimitIcon className={styles["usage-icon"]} />
                )}
              </IconButton>
              <IconButton
                tooltipTitle="Remove group usage limit"
                disabled={!group.limit}
                onClick={() => {
                  if (group.limit) setRemoveLimit(group);
                }}
                className={styles["icon-button"]}
              >
                <RemoveUsageLimitIcon
                  className={styles["usage-icon"]}
                  color={group.limit ? "inherit" : "disabled"}
                />
              </IconButton>
            </Table.Cell>
          </AllowedTo>
        </DataTable.ExpandedRow.Summary>
        <DataTable.ExpandedRow.Details>
          <ProjectsUsage groupId={group.group_id} />
        </DataTable.ExpandedRow.Details>
      </DataTable.ExpandedRow>
    </>
  );
};

export default GroupRow;
